import React, {useState, useEffect, FunctionComponent} from "react";
import {
    Grid,
    Box,
    Typography,
    Container,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {makeStyles} from "@material-ui/core/styles";
import {useHistory, useLocation} from "react-router-dom";
import API from "../../helpers/apiHelpers";
import {userRole} from "../../interfaces";
import AppointmentsReviewNavigation from "../../Components/Navigation/AppointmentsReviewNavigation";
import {useSnackbar} from "notistack";
import DocCard from "../../Components/DocCard";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../redux/store";
import {
    getAppointmentNotes, getPrescriptions,
    patchAppointmentNotes,
    setAppointmentNotes
} from "../../redux/appointment/appointment.actions";
import {reasonsForVisit, reasonForVisitAtlanta} from "../../Constants/reasonsForVisit";
import {visitOutcomes} from "../../Constants/visitOutcomes";
import moment from "moment/moment";
import {colorInputBorder, colorInputBorderGrayish} from "../../Constants/theme";
import {fillingOptions} from "../../Constants/fillingOptions";
import {Document} from "react-pdf";
import {api_getHandouts} from "../../redux/appointment/appointment.api";

interface AppointmentNotesProps {
    userType: userRole
}

interface INote {
    appointmentId: number,
    ownerConcerns: string,
    assessment: string,
    plan: string,
    subjective: string,
    objective: string,
    comments: string,
    visitOutcome?: string,
    otherReasonForVisit: string,
    contactUsInfo: string,
    handouts: object[],
    reasonForVisit: string[],
}

const useStyle = makeStyles((theme) => ({
    wrapper: {
        marginTop: 90,
        marginBottom: 50,
    },
    leftContentWrapper: {
        width: "50%",
        height: "80%",
        marginTop: "20px",
        [theme.breakpoints.up("md")]: {
            marginLeft: "20px",
            marginTop: "0",
        },
    },
    bullet: {
        display: "inline-block",
        margin: "0 2px",
        transform: "scale(0.8)",
    },
    title: {
        fontSize: 14,
    },
    pos: {
        marginBottom: 12,
    },
    cardContent: {
        display: "flex",
        flexDirection: "column",
        width: "260px",
        height: "425px",
        alignItems: "center",
        justifyContent: "space-between"
    },
    docPhoto: {
        display: "flex",
        justifyContent: "center"
    },
    devideBorrder: {
        border: "1px solid #CCCDE5",
        width: "100%"
    },
    prescriptionsBox: {
        border: `1px solid ${colorInputBorderGrayish}`,
        borderRadius: 4,
        padding: 15,
        marginBottom: 15
    },
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        border: "none",
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 2, 2),
    },
    optionBtn: {
        minWidth: 100,
        marginRight: 10,
    }
}));

const AppointmentNotes: FunctionComponent<AppointmentNotesProps> = ({userType = "VET"}: AppointmentNotesProps) => {
    const {state: {appointmentId}} = useLocation<{ appointmentId: number }>();
    const {state: {afterAppt}} = useLocation<{ afterAppt: boolean }>();
    const classes = useStyle();
    const history = useHistory();
    const [docFirstName, setDocFirstName] = useState("");
    const [docLastName, setDocLastName] = useState("");
    const [docEdu, setDocEdu] = useState("");
    const [docImage, setDocImage] = useState("");
    const [docType, setDocType] = useState("");
    const [scheduledDate, setScheduledDate] = useState("");
    const [appointmentType, setAppointmentType] = useState("CHAT_ONLY");
    const [comments, setComments] = useState("");
    // const [followUp, setFollowUp] = useState(false);
    const [existingNoteID, setExistingNoteID] = useState(0);
    const [isCreated, setIsCreated] = useState(false);
    const [fieldsDisabled, setFieldsDisabled] = useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const appointment = useSelector((state: RootState) => state?.appointment.appointment);
    const notes = useSelector((state: RootState) => state?.appointment.notes);

    const user = useSelector((state: RootState) => state?.user);
    const dispatch = useAppDispatch();

    //New SOAP Fields:
    const [ownerConcerns, setOwnerConcerns] = useState("");
    const [subjective, setSubjective] = useState("");
    const [objective, setObjective] = useState("");
    const [assessment, setAssessment] = useState("");
    const [plan, setPlan] = useState("");
    const [result, setResult] = useState(null);
    const [handouts, setHandouts] = useState(null);
    const [handout, setHandout] = useState(null);
    const [reasonForVisit, setReasonForVisit] = useState([]);
    const [otherValue, setOtherValue] = useState("");
    const [medList, setMedList] = useState([]);
    const [outcomeString, setOutcomeString] = useState("");
    const contactUsDefault = appointment.animal?.primaryVetName?.length
        ? "Our normal business hours are 9am - midnight ET. If your pet has a medical emergency after hours, please seek care immediately at the closest veterinary emergency hospital."
        : "Our normal business hours are 9am - midnight EST. If your pet has a medical emergency after hours, and you cannot reach Hello Ralphie's support team at one of the contact methods below, please seek care immediately at the closest veterinary emergency hospital. If you have questions or concerns or need to schedule any follow-up appointments with a Hello Ralphie veterinarian please contact us. You can also schedule appointments directly via the Hello Ralphie App or on the Web.\n\n*Hello Ralphie Contact Information*\n-Email: support@helloralphie.com\n-Phone: (917) 259-0518\n-Website: helloralphie.com (click the chat box in the lower right hand corner)";
    const [contactUsInfo, setContactUsInfo] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);

    //IC COPY
    const [consentForm, setConsentForm] = useState<null | any>(null);
    useEffect(() => {
        if (moment() > moment(appointment.scheduledDate)) {
            const diffInHours = moment().diff(moment(appointment.scheduledDate), "hours");
            if (diffInHours >= 24) {
                setFieldsDisabled(true);
            }
        }
        setScheduledDate(appointment.scheduledDate);
        setAppointmentType(appointment.type);
        API.getProfiles({userId: appointment.vetUserId})
            .then((profile) => {
                const {type, firstname, lastName, education, image} = profile[0];
                setDocFirstName(firstname || "");
                setDocLastName(lastName || "");
                setDocEdu(education || "");
                setDocImage(image || "");
                setDocType(type || "");
            });
        dispatch(getAppointmentNotes({appointmentId: appointment.id}))
            .then(list => {
                if (list.length) {
                    setExistingNoteID(list[0].id);
                    setAssessment(list[0].assessment || "");
                    if (list[0].id !== 0) {
                        setOwnerConcerns(list[0].ownerConcerns);
                    } else {
                        setOwnerConcerns(appointment.description);
                    }
                    setComments(list[0].comments || "");
                    // setFollowUp(list[0].followUp);
                    setSubjective(list[0].subjective || "");
                    setObjective(list[0].objective || "");
                    setReasonForVisit(appointment.animal.primaryVetName === "East Atlanta Animal Clinic" ? reasonForVisitAtlanta : list[0].reasonForVisit.map(item => reasonsForVisit.filter(obj => obj.value === item)[0]) || []);
                    setPlan(list[0].plan || "");
                    setResult(list[0].visitOutcome === "" ? null : visitOutcomes.filter(item => item.value === list[0].visitOutcome)[0]);
                    setOutcomeString(list[0].visitOutcome);
                    setOtherValue(list[0].otherReasonForVisit || "");
                    setContactUsInfo(Object.prototype.hasOwnProperty.call(list[0], "contactUsInfo") ? list[0].contactUsInfo : contactUsDefault);
                    setHandout(list[0]?.handouts[0]);
                } else {
                    setContactUsInfo(contactUsDefault);
                    setOwnerConcerns(appointment.description);
                }
            });
        dispatch(getPrescriptions(appointment.id))
            .then(list => {
                setMedList(list);
            });
        API.getConsentForm(appointment.id).then(res => Object.prototype.hasOwnProperty.call(res, "imageUrl") ? setConsentForm(res.imageUrl) : console.log(res));
    }, []);

    useEffect(() => {
        api_getHandouts()
            .then((res) => {
                setHandouts(res);
            });
    }, []);

    const handleSubmit = async () => {
        let availableToMoveForward = true;

        if (availableToMoveForward) {
            if (existingNoteID !== 0) {
                await dispatch(patchAppointmentNotes(existingNoteID, {
                    ownerConcerns: ownerConcerns,
                    assessment: assessment,
                    plan: plan,
                    subjective: subjective,
                    objective: objective,
                    reasonForVisit: reasonForVisit.map(item => item.value),
                    // followUp,
                    comments: comments,
                    visitOutcome: result !== null ? result?.value : null,
                    otherReasonForVisit: otherValue,
                    contactUsInfo: contactUsInfo,
                    handouts: handout ? [handout] : []
                })).then((res) => {
                    setExistingNoteID(res.id);
                    enqueueSnackbar("Success!", {
                        variant: "success",
                        autoHideDuration: 4000
                    });
                })
                    .catch(() => {
                        enqueueSnackbar("Something went wrong! Please try again.", {
                            variant: "error",
                            autoHideDuration: 4000
                        });
                    });
            } else {
                if (!isCreated) {
                    setIsCreated(true);
                    const data: INote = {
                        appointmentId: appointment.id,
                        ownerConcerns: ownerConcerns,
                        assessment: assessment,
                        plan: plan,
                        subjective: subjective,
                        objective: objective,
                        comments: comments,
                        reasonForVisit: reasonForVisit.map(item => item.value),
                        otherReasonForVisit: otherValue,
                        contactUsInfo: contactUsInfo,
                        handouts: handout ? [handout] : []
                    };
                    if (result) {
                        data.visitOutcome = result?.value;
                    }
                    await dispatch(setAppointmentNotes(data)).then((res) => {
                        setExistingNoteID(res.id);
                        enqueueSnackbar("Success!", {
                            variant: "success",
                            autoHideDuration: 4000
                        });
                    })
                        .catch(() => {
                            enqueueSnackbar("Something went wrong! Please try again.", {
                                variant: "error",
                                autoHideDuration: 4000
                            });
                        });
                }

            }
            history.push("/appointments");
        }
    };

    const autoSaveNotes = async () => {
        if (existingNoteID !== 0) {
            await dispatch(patchAppointmentNotes(existingNoteID, {
                ownerConcerns: ownerConcerns,
                assessment: assessment,
                plan: plan,
                subjective: subjective,
                objective: objective,
                reasonForVisit: reasonForVisit.map(item => item.value),
                // followUp,
                comments: comments,
                visitOutcome: result !== null ? result?.value : null,
                otherReasonForVisit: otherValue,
                contactUsInfo: contactUsInfo,
                handouts: handout ? [handout] : []
            })).then((res) => {
                setExistingNoteID(res.id);
            })
                .catch(() => {
                    enqueueSnackbar("Something went wrong! Please try again.", {
                        variant: "error",
                        autoHideDuration: 4000
                    });
                });
        } else {
            if (!isCreated) {
                setIsCreated(true);
                const data: INote = {
                    appointmentId: appointment.id,
                    ownerConcerns: ownerConcerns,
                    assessment: assessment,
                    plan: plan,
                    subjective: subjective,
                    objective: objective,
                    comments: comments,
                    reasonForVisit: reasonForVisit.map(item => item.value),
                    otherReasonForVisit: otherValue,
                    contactUsInfo: contactUsInfo,
                    handouts: handout ? [handout] : []
                };
                if (result) {
                    data.visitOutcome = result?.value;
                }
                await dispatch(setAppointmentNotes(data)).then((res) => {
                    setExistingNoteID(res.id);
                })
                    .catch(() => {
                        enqueueSnackbar("Something went wrong! Please try again.", {
                            variant: "error",
                            autoHideDuration: 4000
                        });
                    });
            }

        }
    };

    return (
        <Box className={classes.wrapper}>
            <Container maxWidth="lg">
                <Grid container spacing={6}>
                    <Grid item sm={4} xs={12} justify="center">
                        <DocCard docType={docType} docFirstName={docFirstName} docLastName={docLastName} docEdu={docEdu}
                            docImage={docImage} setDocImage={setDocImage} scheduledDate={scheduledDate}
                            appointmentType={appointmentType} animalName={appointment?.animal?.name} dateOfBirth={appointment?.animal?.dateOfBirth}/>
                        {consentForm &&
                        <Button
                            fullWidth={true}
                            color="primary"
                            variant="outlined"
                            onClick={() => window.open(consentForm, "_blank")}
                            style={{marginTop: 10}}
                        >
                            View Consent Form
                        </Button>
                        }
                    </Grid>
                    <Grid item sm={8} xs={12}>
                        <Grid container>
                            <Grid item sm={12} xs={12}>
                                <Box marginBottom="33px">
                                    <Typography variant="h4">
                                        Medical Record - Appointment Notes
                                    </Typography>
                                </Box>
                            </Grid>
                            {user.role === "VET" && afterAppt &&
                            <AppointmentsReviewNavigation/>
                            }
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Reason for Visit
                                </Typography>
                                <Autocomplete
                                    id="reasonForVisit"
                                    multiple
                                    limitTags={2}
                                    disabled={fieldsDisabled || appointment.animal.primaryVetName === "East Atlanta Animal Clinic"}
                                    value={reasonForVisit}
                                    onChange={(event, value) => {
                                        setReasonForVisit(value);
                                    }}
                                    onBlur={() => {autoSaveNotes();}}
                                    options={appointment.animal.primaryVetName === "East Atlanta Animal Clinic" ? reasonForVisitAtlanta : reasonsForVisit}
                                    getOptionLabel={(option) => option.type}
                                    style={{width: "100%", marginBottom: 15}}
                                    renderInput={(params) => <TextField {...params} variant="outlined"/>}
                                />
                            </Grid>
                            {reasonForVisit.some(obj => obj.value === "OTHER") &&
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Other Reason
                                </Typography>
                                <TextField
                                    id="other"
                                    value={otherValue}
                                    disabled={fieldsDisabled}
                                    onChange={(e) => setOtherValue(e.target.value)}
                                    onBlur={() => {autoSaveNotes();}}
                                    variant="outlined"
                                    style={{width: "100%", marginBottom: 15}}
                                />
                            </Grid>
                            }
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Owner Concerns
                                </Typography>
                                <TextField
                                    id="ownerConcerns"
                                    multiline
                                    disabled={fieldsDisabled}
                                    value={ownerConcerns}
                                    onChange={(e) => setOwnerConcerns(e.target.value)}
                                    onBlur={() => {autoSaveNotes();}}
                                    style={{width: "100%", marginBottom: 15}}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Subjective
                                </Typography>
                                <TextField
                                    id="subjective"
                                    multiline
                                    value={subjective}
                                    disabled={fieldsDisabled}
                                    onChange={e => setSubjective(e.target.value)}
                                    onBlur={() => {autoSaveNotes();}}
                                    style={{width: "100%", marginBottom: 15}}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Objective
                                </Typography>
                                <TextField
                                    id="objective"
                                    multiline
                                    disabled={fieldsDisabled}
                                    value={objective}
                                    onChange={(e) => setObjective(e.target.value)}
                                    onBlur={() => {autoSaveNotes();}}
                                    style={{width: "100%", marginBottom: 15}}
                                    variant="outlined"
                                />
                            </Grid>
                            <>
                                <Grid item sm={12} xs={12}>
                                    <Typography variant="h4">
                                        Assessment
                                    </Typography>
                                    <TextField
                                        id="assessment"
                                        multiline
                                        disabled={fieldsDisabled}
                                        value={assessment}
                                        onChange={(e) => setAssessment(e.target.value)}
                                        onBlur={() => {autoSaveNotes();}}
                                        style={{width: "100%", marginBottom: 15}}
                                        variant="outlined"
                                    />
                                </Grid>
                            </>
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Plan
                                </Typography>
                                <TextField
                                    id="plan"
                                    multiline
                                    disabled={fieldsDisabled}
                                    value={plan}
                                    onChange={(e) => setPlan(e.target.value)}
                                    onBlur={() => {autoSaveNotes();}}
                                    style={{width: "100%", marginBottom: 15}}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Contact Information
                                </Typography>
                                <TextField
                                    id="contactUsInfo"
                                    multiline
                                    disabled={fieldsDisabled}
                                    value={contactUsInfo}
                                    onChange={(e) => setContactUsInfo(e.target.value)}
                                    onBlur={() => {autoSaveNotes();}}
                                    style={{width: "100%", marginBottom: 15}}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Submitted RX/OTC
                                </Typography>
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Box display="flex" flexDirection="column" justifyContent="center"
                                    alignItems="flex-start" className={classes.prescriptionsBox}>
                                    {
                                        medList.length ?
                                            medList.map((med, idx) => {
                                                return (
                                                    <>
                                                        <Typography color="textSecondary"
                                                            key={`${idx.toString()} ${med.name}`}>
                                                            {med.name}, {med.strength}<br/>
                                                            {med.quantity}<br/>
                                                            {med.labelInstructions}<br/>
                                                            {med.refills}<br/>
                                                            {fillingOptions.find(option => option.value === med.fillingOption).label}<br/>
                                                            {med.notes ? med.notes : null}
                                                        </Typography>
                                                        {idx === (medList.length - 1) ? null : <br/>}
                                                    </>
                                                );
                                            }) : <Typography color="textSecondary">No submitted RX/OTC</Typography>
                                    }
                                </Box>
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Comments
                                </Typography>
                                <TextField
                                    id="comments"
                                    multiline
                                    // disabled={fieldsDisabled}
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                    onBlur={() => {autoSaveNotes();}}
                                    style={{width: "100%", marginBottom: 15}}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Outcome
                                </Typography>
                                <Autocomplete
                                    id="outcome"
                                    options={visitOutcomes}
                                    value={result}
                                    disabled={fieldsDisabled}
                                    onChange={(event, value) => {
                                        setResult(value);
                                    }}
                                    onBlur={() => {autoSaveNotes();}}
                                    getOptionLabel={(option) => option.label}
                                    style={{width: "100%", marginBottom: 20}}
                                    renderInput={(params) => <TextField {...params} variant="outlined"/>}
                                />
                            </Grid>
                            {handouts?.length
                            &&
                            <Grid item sm={12} xs={12}>
                                <Typography variant="h4">
                                    Handout
                                </Typography>
                                <Autocomplete
                                    id="handout"
                                    options={handouts}
                                    value={handout}
                                    disabled={fieldsDisabled}
                                    onChange={(e, value) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setHandout(value);
                                    }}
                                    onBlur={() => {autoSaveNotes();}}
                                    getOptionLabel={(option) => option?.name}
                                    style={{width: "100%", marginBottom: 20}}
                                    renderInput={(params) => (
                                        <TextField {...params} variant="outlined"/>
                                    )}
                                    renderOption={(option) => {
                                        const isSelected = option?.name === handout?.name;
                                        return (
                                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                <Box marginRight="3px">
                                                    {option?.name}
                                                </Box>
                                                <Box>
                                                    {option?.path
                                                    &&
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        className={classes.optionBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open(option.path);
                                                        }}
                                                    >
                                                        View
                                                    </Button>}
                                                    <Button
                                                        variant={isSelected ? "contained" : "outlined"}
                                                        color="primary"
                                                        className={classes.optionBtn}
                                                        onClick={(e) => {
                                                            setHandout(isSelected ? null : option);
                                                        }}
                                                    >
                                                        {isSelected ? "Deselect" : "Select"}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        );
                                    }}
                                />
                            </Grid>}
                            <Grid item sm={12} xs={12}>
                                <Button variant="contained" color="primary" fullWidth onClick={handleSubmit}>Save and
                                    Close</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default AppointmentNotes;
